import React, { useState, useEffect, useRef } from 'react';
import { getActiveArtworks, techniqueTr, legalMentionTr } from '../data/shopArtworks';
import { useLanguage } from '../context/LanguageContext';

// Regex stricte de validation d'email (RFC 5322 simplifiée, exige un TLD valide)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const HauteGalerie = () => {
  const { t, language } = useLanguage();
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const artworksPerPage = 10;
  const galerieTopRef = useRef(null);

  // États du formulaire de contact (acquisition)
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactStatus, setContactStatus] = useState(''); // '', 'sending', 'success', 'error'
  const [emailError, setEmailError] = useState('');

  // Pré-remplir le sujet avec le titre de l'œuvre quand la modale s'ouvre
  useEffect(() => {
    if (showContactModal && selectedArtwork) {
      setContactForm({
        name: '',
        email: '',
        subject: `Acquisition : ${getTranslatedTitle(selectedArtwork.title)}`,
        message: ''
      });
      setContactStatus('');
      setEmailError('');
    }
  }, [showContactModal, selectedArtwork, language]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');

    // Validation stricte de l'email
    const emailTrimmed = contactForm.email.trim();
    if (!EMAIL_REGEX.test(emailTrimmed)) {
      setEmailError("Format d'email invalide. Exemple attendu : prenom.nom@domaine.com");
      return;
    }

    setContactStatus('sending');
    try {
      const payload = {
        name: contactForm.name.trim(),
        email: emailTrimmed,
        subject: contactForm.subject.trim() || (selectedArtwork ? `Acquisition : ${getTranslatedTitle(selectedArtwork.title)}` : 'Demande depuis la Haute Galerie'),
        message: selectedArtwork
          ? `[Demande d'acquisition — Œuvre : ${getTranslatedTitle(selectedArtwork.title)} | Dimensions : ${selectedArtwork.dimensions} | Prix : ${selectedArtwork.price}€ | ID : ${selectedArtwork.id}]\n\n${contactForm.message.trim()}`
          : contactForm.message.trim()
      };
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setContactStatus('success');
        setContactForm({ name: '', email: '', subject: '', message: '' });
        // Ferme automatiquement la modale après 2.5s pour une UX fluide
        setTimeout(() => {
          setShowContactModal(false);
          setContactStatus('');
        }, 2500);
      } else {
        setContactStatus('error');
      }
    } catch (err) {
      setContactStatus('error');
    }
  };

  // Obtenir toutes les œuvres actives et les trier par prix décroissant
  const allArtworks = getActiveArtworks().sort((a, b) => b.price - a.price);
  
  // Calculer la pagination
  const totalPages = Math.ceil(allArtworks.length / artworksPerPage);
  const indexOfLastArtwork = currentPage * artworksPerPage;
  const indexOfFirstArtwork = indexOfLastArtwork - artworksPerPage;
  const artworks = allArtworks.slice(indexOfFirstArtwork, indexOfLastArtwork);

  // Fonction pour changer de page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll vers le haut de la galerie
    if (galerieTopRef.current) {
      galerieTopRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Helper pour obtenir le titre traduit
  const getTranslatedTitle = (titleObj) => {
    if (typeof titleObj === 'string') return titleObj;
    return titleObj[language] || titleObj.fr;
  };

  // Calculer le ratio aspect basé sur les dimensions réelles
  // Format des dimensions : Hauteur x Largeur
  const getAspectRatio = (dimensions) => {
    const matches = dimensions.match(/([\d,]+)\s*x\s*([\d,]+)/i);
    if (matches) {
      const height = parseFloat(matches[1].replace(',', '.'));
      const width = parseFloat(matches[2].replace(',', '.'));
      return width / height;
    }
    return 1; // ratio carré par défaut
  };

  // Bloquer le scroll quand la modal est ouverte
  useEffect(() => {
    if (selectedArtwork) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedArtwork]);

  // Vue principale - Liste continue de toutes les œuvres
  return (
    <div className="min-h-screen py-24 px-4" style={{ backgroundColor: '#081429' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-light mb-6 text-blue-400 tracking-wide">
            {t('shop.title')}
          </h1>
          <p className="text-xl max-w-2xl mx-auto font-light text-white">
            {t('shop.subtitle')}
          </p>
          <div className="mt-6 text-base font-medium text-white">
            {allArtworks.length} {t('shop.artworksCount')} | Page {currentPage} sur {totalPages}
          </div>
        </div>

        {/* Référence pour le scroll - Juste avant la grille */}
        <div ref={galerieTopRef} className="h-1" />

        {/* Grille d'œuvres - Design minimaliste */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {artworks.map((artwork) => (
            <div
              key={artwork.id}
              onClick={() => setSelectedArtwork(artwork)}
              className="group cursor-pointer transition-all duration-300 hover:shadow-xl bg-slate-900/40 border border-white/5"
            >
              <div className="aspect-square overflow-hidden bg-slate-800/30">
                <img
                  src={artwork.image}
                  alt={`${getTranslatedTitle(artwork.title)} - Georg Esco - Haute Galerie - Œuvre d'art contemporain de prestige - Peinture originale ${artwork.dimensions}`}
                  className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-5 text-center">
                <h3 className="text-base font-normal mb-3 text-blue-500 line-clamp-2">
                  {getTranslatedTitle(artwork.title)}
                </h3>
                <p className="text-xl mb-3 font-light text-white">
                  {artwork.dimensions}
                </p>
                <p className="text-sm font-light text-white">
                  {t('shop.contactArtist')}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* PAGINATION - Contrôles de navigation */}
        <div className="flex items-center justify-center gap-2 py-8">
          {/* Bouton Précédent */}
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg font-light transition-all ${
              currentPage === 1
                ? 'bg-slate-800/40 text-gray-600 cursor-not-allowed'
                : 'bg-slate-900/60 text-blue-400 hover:bg-slate-900/80'
            }`}
          >
            ◄ Précédent
          </button>

          {/* Numéros de pages */}
          <div className="flex gap-2">
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  onClick={() => paginate(pageNumber)}
                  className={`w-10 h-10 rounded-lg font-light transition-all ${
                    currentPage === pageNumber
                      ? 'bg-blue-400 text-slate-950 font-medium'
                      : 'bg-slate-900/60 text-white hover:bg-slate-900/80'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          {/* Bouton Suivant */}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg font-light transition-all ${
              currentPage === totalPages
                ? 'bg-slate-800/40 text-gray-600 cursor-not-allowed'
                : 'bg-slate-900/60 text-blue-400 hover:bg-slate-900/80'
            }`}
          >
            Suivant ►
          </button>
        </div>
      </div>

      {/* MODAL FIXE ET CENTRÉE - S'ouvre toujours au même endroit */}
      {selectedArtwork && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(2, 6, 23, 0.95)',
            backdropFilter: 'blur(8px)'
          }}
          onClick={() => setSelectedArtwork(null)}
        >
          {/* Bouton de fermeture */}
          <button
            onClick={() => setSelectedArtwork(null)}
            className="fixed top-4 right-4 z-50 text-white hover:text-blue-400 p-3 bg-slate-900/60 hover:bg-slate-900/80 rounded-full transition-all shadow-xl"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Contenu de la modal */}
          <div 
            className="relative max-w-6xl w-full max-h-[90vh] overflow-y-auto bg-slate-900/40 backdrop-blur-sm rounded-xl p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Schema.org JSON-LD pour Google Lens - Protection Propriété Intellectuelle */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "VisualArtwork",
                "name": getTranslatedTitle(selectedArtwork.title),
                "creator": {
                  "@type": "Person",
                  "name": "Georg Esco",
                  "url": "https://georg-esco.com",
                  "sameAs": [
                    "https://www.instagram.com/georg.esco",
                    "https://www.facebook.com/georg.esco.artist"
                  ]
                },
                "copyrightHolder": {
                  "@type": "Person",
                  "name": "Georg Esco"
                },
                "copyrightYear": selectedArtwork.year || "2024-2025",
                "image": selectedArtwork.image,
                "artMedium": techniqueTr[language] || techniqueTr.fr,
                "artform": "Peinture",
                "dateCreated": selectedArtwork.year || "2024-2025",
                "width": selectedArtwork.dimensions?.split('x')[1]?.trim() || "",
                "height": selectedArtwork.dimensions?.split('x')[0]?.trim() || "",
                "license": "https://creativecommons.org/licenses/by-nc-nd/4.0/",
                "creditText": "© Georg Esco - All Rights Reserved"
              })}
            </script>
            <div className="grid md:grid-cols-2 gap-8 md:gap-16">
              {/* Image avec proportions exactes - RESPECTE LE RATIO ORIGINAL */}
              <div className="relative flex items-center justify-center min-h-[400px] max-h-[70vh]">
                <div 
                  className="overflow-hidden cursor-zoom-in flex items-center justify-center"
                  onMouseEnter={() => setIsZoomed(true)}
                  onMouseLeave={() => setIsZoomed(false)}
                >
                  <img
                    src={selectedArtwork.image}
                    alt={`${getTranslatedTitle(selectedArtwork.title)} - Georg Esco - Haute Galerie - Œuvre d'art contemporain de prestige - Peinture originale ${selectedArtwork.dimensions} - Collection exclusive internationale`}
                    className={`object-contain transition-transform duration-500 ${isZoomed ? 'scale-150' : 'scale-100'}`}
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                </div>
                {isZoomed && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded text-sm font-light">
                    {t('shop.hoverToZoom')}
                  </div>
                )}
                {/* Copyright Notice */}
                <div className="text-center mt-4">
                  <p className="text-xs font-light text-gray-400 italic">
                    {t('shop.copyright')}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="flex flex-col justify-center">
                <h1 className="text-3xl md:text-4xl font-light mb-6 md:mb-8 text-blue-500">
                  {getTranslatedTitle(selectedArtwork.title)}
                </h1>
                
                <div className="space-y-4 md:space-y-6 mb-8 md:mb-10">
                  <div>
                    <span className="text-sm text-gray-400 uppercase tracking-wider font-light block mb-2">
                      {t('shop.dimensions')}
                    </span>
                    <p className="text-2xl md:text-3xl font-light text-white">
                      {selectedArtwork.dimensions}
                    </p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-400 uppercase tracking-wider font-light block mb-2">
                      {t('shop.technique')}
                    </span>
                    <p className="text-base md:text-lg font-light text-white">
                      {selectedArtwork.technique && typeof selectedArtwork.technique === 'object'
                        ? (selectedArtwork.technique[language] || selectedArtwork.technique.fr)
                        : (techniqueTr[language] || techniqueTr.fr)
                      }
                    </p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-400 uppercase tracking-wider font-light block mb-2">
                      {t('shop.price')}
                    </span>
                    <p className="text-base md:text-lg font-light text-white">
                      {t('shop.contactArtist')}
                    </p>
                  </div>
                </div>

                {/* Legal Mention */}
                <p className="text-sm italic font-light leading-relaxed mb-6 md:mb-8" style={{ color: '#60A5FA' }}>
                  {selectedArtwork.legalMention && typeof selectedArtwork.legalMention === 'object' 
                    ? (selectedArtwork.legalMention[language] || selectedArtwork.legalMention.fr) 
                    : (legalMentionTr[language] || legalMentionTr.fr)}
                </p>

                {/* Acquisition Button - Bijou Discret Luxury */}
                <div className="flex justify-center mb-3 md:mb-4">
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="text-center text-sm md:text-base font-light tracking-widest transition-all duration-500 px-8 py-2 rounded-md hover:shadow-lg"
                    style={{ 
                      background: 'linear-gradient(135deg, #E8E5E0 0%, #D4CFC8 100%)',
                      color: '#3E3A36',
                      border: '1px solid rgba(212, 175, 55, 0.15)',
                      fontWeight: '300',
                      letterSpacing: '0.2em',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      minWidth: '280px',
                      maxWidth: '320px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #C9B896 0%, #B4A68F 100%)';
                      e.currentTarget.style.color = '#FFFFFF';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(201, 184, 150, 0.35)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #E8E5E0 0%, #D4CFC8 100%)';
                      e.currentTarget.style.color = '#3E3A36';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {t('shop.acquisitionButton')}
                  </button>
                </div>

                {/* Réassurance - Ultra discrète */}
                <p className="text-center text-xs font-light italic text-gray-500 mb-3 px-4">
                  {t('shop.secureShipping')} · {t('shop.certificateOfAuthenticity')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal Pop-up - Luxury Design */}
      {showContactModal && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
          onClick={() => setShowContactModal(false)}
        >
          <div 
            className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            style={{ 
              border: '1px solid rgba(212, 175, 55, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#D4AF37'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.transform = 'rotate(0deg)';
              }}
            >
              <span className="text-2xl font-light">×</span>
            </button>

            {/* Modal Header */}
            <div className="p-8 pb-6 border-b border-white/10">
              <h2 className="text-3xl md:text-4xl font-light tracking-wider text-center mb-2" style={{ color: '#C9B896' }}>
                {t('contact.title')}
              </h2>
              {selectedArtwork && (
                <p className="text-center text-sm font-light text-gray-400 mt-2">
                  Concernant : <span className="text-blue-400">{getTranslatedTitle(selectedArtwork.title)}</span>
                </p>
              )}
              <p className="text-center text-gray-400 text-sm font-light mt-3 italic">
                {t('contact.subtitle')}
              </p>
            </div>

            {/* Contact Form */}
            <div className="p-8">
              <form className="space-y-6" onSubmit={handleContactSubmit} noValidate data-testid="haute-galerie-contact-form">
                <div>
                  <label className="block text-sm font-light text-gray-300 mb-2 tracking-wide">
                    {t('contact.name')}
                  </label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    data-testid="hg-contact-name"
                    className="w-full px-4 py-3 rounded-lg font-light text-white transition-all duration-300"
                    style={{ 
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '1px solid rgba(212, 175, 55, 0.5)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Votre nom complet"
                  />
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-300 mb-2 tracking-wide">
                    {t('contact.email')}
                  </label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => {
                      setContactForm({ ...contactForm, email: e.target.value });
                      if (emailError) setEmailError('');
                    }}
                    data-testid="hg-contact-email"
                    aria-invalid={!!emailError}
                    className="w-full px-4 py-3 rounded-lg font-light text-white transition-all duration-300"
                    style={{ 
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: emailError ? '1px solid rgba(239, 68, 68, 0.7)' : '1px solid rgba(255, 255, 255, 0.1)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      if (!emailError) {
                        e.target.style.border = '1px solid rgba(212, 175, 55, 0.5)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!emailError) {
                        e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                    placeholder="votre@email.com"
                  />
                  {emailError && (
                    <p className="mt-2 text-sm font-light" style={{ color: '#ef4444' }} data-testid="hg-email-error">
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-300 mb-2 tracking-wide">
                    {t('contact.subject')}
                  </label>
                  <input
                    type="text"
                    required
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    data-testid="hg-contact-subject"
                    className="w-full px-4 py-3 rounded-lg font-light text-white transition-all duration-300"
                    style={{ 
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '1px solid rgba(212, 175, 55, 0.5)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Objet de votre message"
                  />
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-300 mb-2 tracking-wide">
                    {t('contact.message')}
                  </label>
                  <textarea
                    rows="5"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    data-testid="hg-contact-message"
                    className="w-full px-4 py-3 rounded-lg font-light text-white transition-all duration-300 resize-none"
                    style={{ 
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '1px solid rgba(212, 175, 55, 0.5)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Parlez-nous de votre intérêt pour cette œuvre..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={contactStatus === 'sending'}
                  data-testid="hg-contact-submit"
                  className="w-full py-4 rounded-lg font-light tracking-wider transition-all duration-500 text-lg"
                  style={{ 
                    background: 'linear-gradient(135deg, #C9B896 0%, #B4A68F 100%)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    letterSpacing: '0.15em',
                    boxShadow: '0 4px 12px rgba(201, 184, 150, 0.3)',
                    opacity: contactStatus === 'sending' ? 0.6 : 1,
                    cursor: contactStatus === 'sending' ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (contactStatus === 'sending') return;
                    e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #C9B896 100%)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.5)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #C9B896 0%, #B4A68F 100%)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(201, 184, 150, 0.3)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {contactStatus === 'sending' ? 'Envoi en cours…' : t('contact.send')}
                </button>

                {contactStatus === 'success' && (
                  <div
                    className="flex items-center justify-center gap-3 py-4 px-6 rounded-lg animate-fade-in"
                    style={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      color: '#10b981'
                    }}
                    data-testid="hg-contact-success"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span className="text-base font-light tracking-wide">
                      {t('contact.success')}
                    </span>
                  </div>
                )}
                {contactStatus === 'error' && (
                  <p className="text-center text-sm font-light" style={{ color: '#ef4444' }} data-testid="hg-contact-error">
                    {t('contact.error')}
                  </p>
                )}

                <p className="text-center text-xs text-gray-500 font-light italic">
                  Réponse sous 24-48h • Toute demande est traitée avec la plus grande confidentialité
                </p>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HauteGalerie;
